import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Hash, ArrowLeft, Loader2, Check, Trophy, LogOut, Sparkles } from "lucide-react";
import { useRoom, BACKEND } from "@/useRoom";
import { play } from "@/sounds";
import { SparkleBurst } from "@/components/interactions";

const API = `${BACKEND}/api`;
const WS = "/api/uno/ws";
const SKEY = "serie_session";
const CARD_COLORS = ["#e4572e", "#2e86ab", "#17a398", "#f3a712", "#8e44ad"];
const SPECIALS = [
  { id: "wild", emoji: "🃏", label: { es: "Comodín", en: "Wild" }, bg: "#1b1b24", ring: "#f3a712" },
  { id: "reverse", emoji: "🔄", label: { es: "Reversa", en: "Reverse" }, bg: "#17a398", needTarget: false },
  { id: "skip", emoji: "⛔", label: { es: "Salta", en: "Skip" }, bg: "#2e86ab", needTarget: true },
  { id: "draw2", emoji: "+2", label: { es: "Roba 2", en: "Draw 2" }, bg: "#e4572e", needTarget: true },
  { id: "draw4", emoji: "+4", label: { es: "Roba 4", en: "Draw 4" }, bg: "#8e44ad", needTarget: true },
];

const L = {
  es: { tag: "Serie Secreta", enterCode: "Código de la mesa", name: "Tu nombre", join: "Entrar",
    back: "Volver", waiting: "Esperando a que empiece la partida…", waitingSub: "El anfitrión iniciará desde la TV",
    yourSeries: "Tu serie secreta", instructions: "Saca cartas UNO y toca cada número cuando lo encuentres.",
    complete: "¡Completaste tu serie!", youWin: "¡GANASTE! 🎉", winner: "Ganó", tapHint: "Toca para marcar",
    found: "encontrados", notFound: "Mesa no encontrada o partida en curso", leave: "Salir", playAgain: "Esperando nueva partida…",
    specials: "Cartas especiales", wildHint: "Toca el número que quieres marcar 🃏", chooseTarget: "¿A quién se la aplicas? (en secreto 🤫)", cancel: "Cancelar" },
  en: { tag: "Secret Series", enterCode: "Table code", name: "Your name", join: "Join",
    back: "Back", waiting: "Waiting for the game to start…", waitingSub: "The host will start from the TV",
    yourSeries: "Your secret series", instructions: "Draw UNO cards and tap each number when you find it.",
    complete: "You completed your series!", youWin: "YOU WIN! 🎉", winner: "Winner", tapHint: "Tap to mark",
    found: "found", notFound: "Table not found or game in progress", leave: "Leave", playAgain: "Waiting for a new game…",
    specials: "Special cards", wildHint: "Tap the number you want to mark 🃏", chooseTarget: "Apply it to whom? (secretly 🤫)", cancel: "Cancel" },
};

export default function UnoPlay() {
  const nav = useNavigate();
  const [lang, setLang] = useState("es");
  const [session, setSession] = useState(() => { try { return JSON.parse(localStorage.getItem(SKEY) || "null"); } catch { return null; } });
  const t = L[lang];

  const onJoined = (s) => { localStorage.setItem(SKEY, JSON.stringify(s)); setSession(s); };
  const onLeave = () => { localStorage.removeItem(SKEY); setSession(null); };

  if (!session) return <JoinForm lang={lang} setLang={setLang} t={t} nav={nav} onJoined={onJoined} />;
  return <PlayerGame session={session} lang={lang} t={t} onLeave={onLeave} />;
}

function JoinForm({ lang, setLang, t, nav, onJoined }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (code.length < 4 || !name.trim()) return;
    setBusy(true); setErr("");
    try {
      const res = await axios.post(`${API}/uno/rooms/join`, { code, name: name.trim() });
      onJoined({ code: res.data.code, pid: res.data.player_id, name: name.trim() });
    } catch { setErr(t.notFound); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grain flex flex-col items-center justify-center px-6" style={{ background: "radial-gradient(circle at 30% 20%, #10243a, #0b1220 70%)" }}>
      <button onClick={() => nav("/serie")} className="absolute top-6 left-6 inline-flex items-center gap-2 text-sand/70 hover:text-white text-sm"><ArrowLeft className="w-4 h-4" /> {t.back}</button>
      <div className="absolute top-6 right-6 flex gap-2">
        {["es", "en"].map((l) => <button key={l} onClick={() => setLang(l)} className={`px-3 py-1.5 rounded-full text-sm border ${lang === l ? "bg-[#2e86ab] text-white border-[#2e86ab]" : "glass text-parchment border-white/20"}`}>{l === "es" ? "ES" : "EN"}</button>)}
      </div>
      <form onSubmit={submit} className="glass rounded-3xl p-8 border-[#2e86ab]/40 w-full max-w-sm" data-testid="unoplay-join-form">
        <div className="text-center mb-6"><Hash className="w-12 h-12 text-[#f3a712] mx-auto mb-2" /><h1 className="font-serif text-2xl text-white">{t.tag}</h1></div>
        <label className="block mb-4">
          <span className="text-xs uppercase tracking-widest text-sand/60">{t.enterCode}</span>
          <input data-testid="unoplay-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" placeholder="1234"
            className="w-full mt-1 bg-charcoal/70 border border-white/20 rounded-xl px-4 py-3 text-white text-2xl tracking-[0.4em] text-center outline-none focus:border-[#f3a712]" />
        </label>
        <label className="block mb-4">
          <span className="text-xs uppercase tracking-widest text-sand/60">{t.name}</span>
          <input data-testid="unoplay-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={20}
            className="w-full mt-1 bg-charcoal/70 border border-white/20 rounded-xl px-4 py-3 text-white outline-none focus:border-[#f3a712]" />
        </label>
        {err && <p className="text-incorrect text-sm mb-3" data-testid="unoplay-error">{err}</p>}
        <button data-testid="unoplay-join-btn" disabled={busy || code.length < 4 || !name.trim()} type="submit"
          className="btn-tactile w-full bg-[#f3a712] text-midnight border-[#c98f0c] font-bold text-lg rounded-2xl px-6 py-3 disabled:opacity-40">
          {busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : t.join}
        </button>
      </form>
    </div>
  );
}

function PlayerGame({ session, lang, t, onLeave }) {
  const { code, pid, name } = session;
  const { state, send, connected } = useRoom(code, "player", pid, WS);
  const [burst, setBurst] = useState(0);
  const [wildMode, setWildMode] = useState(false);
  const [targetCard, setTargetCard] = useState(null);

  const me = state?.players?.find((p) => p.id === pid);
  const series = me?.series || [];
  const found = me?.found || [];
  const foundCount = found.filter(Boolean).length;
  const iWon = state?.winner === pid;
  const complete = series.length > 0 && foundCount === series.length;
  const others = (state?.players || []).filter((p) => p.id !== pid);

  useEffect(() => { if (iWon) play("victory"); }, [iWon]);

  const toggle = (i) => {
    const next = !found[i];
    if (next) { play("correct"); setBurst(Date.now()); } else play("page");
    send({ action: "found", index: i, value: next });
  };

  const onCardTap = (i) => {
    if (!state || state.status !== "playing") return;
    if (wildMode) {
      if (!found[i]) { play("recovered"); setBurst(Date.now()); send({ action: "use_card", card: "wild", index: i }); }
      setWildMode(false);
      return;
    }
    toggle(i);
  };

  const onSpecial = (card) => {
    if (!state || state.status !== "playing") return;
    setTargetCard(null);
    if (card === "wild") { setWildMode((w) => !w); return; }
    setWildMode(false);
    if (card === "reverse") { play("incorrect"); send({ action: "use_card", card: "reverse" }); return; }
    setTargetCard(card); // skip / draw2 / draw4 need a target
  };

  const applyTarget = (tpid) => {
    play("incorrect");
    send({ action: "use_card", card: targetCard, target: tpid });
    setTargetCard(null);
  };

  if (state?.error) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-midnight text-sand gap-4 px-6 text-center">
      <p>{t.notFound}</p>
      <button onClick={onLeave} className="text-[#f3a712] underline">{t.back}</button>
    </div>;
  }

  return (
    <div className="min-h-screen grain flex flex-col text-white" style={{ background: "radial-gradient(circle at 50% 0%, #10243a, #0b1220 70%)" }} data-testid="unoplay-game">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2 text-[#5fb0d4]"><Hash className="w-5 h-5" /><span className="font-display text-sm tracking-widest">{code}</span><span className={`w-2 h-2 rounded-full ${connected ? "bg-correct" : "bg-incorrect"}`} /></div>
        <span className="text-sand/80 text-sm font-semibold truncate max-w-[45%]">{name}</span>
        <button data-testid="unoplay-leave" onClick={onLeave} className="text-sand/50 hover:text-incorrect"><LogOut className="w-4 h-4" /></button>
      </div>

      {(!state || state.status === "lobby") && (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
          <Loader2 className="w-10 h-10 animate-spin text-[#f3a712] mb-4" />
          <h2 className="font-serif text-2xl text-white">{t.waiting}</h2>
          <p className="text-sand/60 mt-2">{t.waitingSub}</p>
        </div>
      )}

      {state && (state.status === "playing" || state.status === "finished") && series.length > 0 && (
        <div className="flex-1 flex flex-col items-center px-5 py-6">
          {iWon ? (
            <div className="relative text-center mb-6"><SparkleBurst count={36} /><h2 className="font-display font-800 text-4xl text-[#f3a712] flex items-center gap-2"><Trophy className="w-9 h-9" /> {t.youWin}</h2></div>
          ) : state.status === "finished" ? (
            <div className="text-center mb-6"><h2 className="font-serif text-2xl text-sand">{t.winner}: <span className="text-[#f3a712] font-bold">{state.winner_name}</span></h2></div>
          ) : complete ? (
            <div className="text-center mb-6"><h2 className="font-serif text-2xl text-[#f3a712] flex items-center gap-2"><Sparkles className="w-6 h-6" /> {t.complete}</h2></div>
          ) : (
            <div className="text-center mb-5">
              <h2 className="font-serif text-2xl text-white">{t.yourSeries}</h2>
              <p className="text-sand/60 text-sm mt-1 max-w-xs mx-auto">{t.instructions}</p>
              <p className="text-[#f3a712] text-sm font-bold mt-2">{foundCount}/{series.length} {t.found}</p>
            </div>
          )}

          <div className={`grid grid-cols-5 gap-2.5 w-full max-w-md ${wildMode ? "ring-2 ring-[#f3a712] rounded-2xl p-1 animate-pulse-gold" : ""}`} key={burst}>
            {series.map((num, i) => {
              const isFound = found[i];
              const color = CARD_COLORS[i % CARD_COLORS.length];
              return (
                <button key={i} data-testid={`unoplay-card-${i}`} disabled={state.status === "finished"}
                  onClick={() => onCardTap(i)}
                  className={`relative aspect-[2/3] rounded-2xl btn-tactile flex items-center justify-center transition-all ${isFound ? "scale-100" : "opacity-95"}`}
                  style={isFound
                    ? { background: color, boxShadow: `0 8px 24px ${color}66`, border: "4px solid white" }
                    : { background: "#141c2e", border: "3px dashed rgba(255,255,255,0.25)" }}>
                  <span className={`font-display font-800 ${isFound ? "text-white text-5xl" : "text-sand/70 text-4xl"}`} style={isFound ? { textShadow: "0 2px 0 rgba(0,0,0,0.25)" } : {}}>{num}</span>
                  {isFound && <span className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white flex items-center justify-center"><Check className="w-4 h-4" style={{ color }} /></span>}
                </button>
              );
            })}
          </div>

          {state.status === "playing" && (
            <div className="w-full max-w-md mt-6" data-testid="unoplay-specials">
              {wildMode
                ? <p className="text-center text-[#f3a712] text-sm font-bold mb-3 animate-pulse">{t.wildHint}</p>
                : <p className="text-center text-sand/50 text-xs uppercase tracking-widest mb-3">{t.specials}</p>}
              <div className="grid grid-cols-5 gap-2">
                {SPECIALS.map((s) => (
                  <button key={s.id} data-testid={`unoplay-special-${s.id}`} onClick={() => onSpecial(s.id)}
                    className={`aspect-[2/3] rounded-xl btn-tactile flex flex-col items-center justify-center gap-1 border-2 transition-all ${(s.id === "wild" && wildMode) ? "ring-2 ring-[#f3a712] scale-105" : ""}`}
                    style={{ background: s.bg, borderColor: s.id === "wild" ? "#f3a712" : "rgba(255,255,255,0.3)" }}>
                    <span className="font-display font-800 text-white text-xl leading-none" style={{ textShadow: "0 1px 0 rgba(0,0,0,0.3)" }}>{s.emoji}</span>
                    <span className="text-white/90 text-[9px] leading-tight text-center px-0.5">{s.label[lang]}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {!complete && state.status === "playing" && <p className="text-sand/40 text-xs mt-6">{t.tapHint}</p>}
          {state.status === "finished" && !iWon && <p className="text-sand/50 text-sm mt-8">{t.playAgain}</p>}
        </div>
      )}

      {targetCard && (
        <div className="fixed inset-0 z-50 bg-midnight/80 backdrop-blur flex items-end sm:items-center justify-center p-5" data-testid="unoplay-target-modal" onClick={() => setTargetCard(null)}>
          <div className="glass rounded-3xl p-6 w-full max-w-sm border-2 border-[#f3a712]/40" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-4">
              <div className="text-4xl mb-2">{SPECIALS.find((s) => s.id === targetCard)?.emoji}</div>
              <p className="font-serif text-xl text-white">{t.chooseTarget}</p>
            </div>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {others.length === 0 && <p className="text-sand/50 text-center text-sm py-4">—</p>}
              {others.map((p) => (
                <button key={p.id} data-testid={`unoplay-target-${p.id}`} onClick={() => applyTarget(p.id)}
                  className="btn-tactile w-full flex items-center gap-3 glass rounded-2xl px-4 py-3 border hover:border-[#f3a712]" style={{ borderColor: `${p.color}66` }}>
                  <span className="w-9 h-9 rounded-full shrink-0" style={{ background: p.color }} />
                  <span className="font-serif text-lg text-white flex-1 text-left truncate">{p.name}</span>
                  <span className="text-sand/50 text-xs">{p.found_count}/{p.total}</span>
                </button>
              ))}
            </div>
            <button data-testid="unoplay-target-cancel" onClick={() => setTargetCard(null)} className="w-full mt-4 text-sand/60 hover:text-white text-sm">{t.cancel}</button>
          </div>
        </div>
      )}
    </div>
  );
}
